import React, { useContext } from 'react'
import { marginSizes } from '@jarvis.oddle/core/theme/default'
import { getOrderedAddressFields } from '@jarvis.oddle/core/tools/address'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Divider from '@jarvis.oddle/core/components/Divider'
import { P } from '@jarvis.oddle/core/components/Typo'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { CardHeader, CheckBox, Section } from './Elements'

interface AddressConfig {
  heading: string
  optional: string
  billingFieldName: string
  deliveryFieldName: string
}

export const addressConfig = {
  addressOne: (t): AddressConfig => ({
    heading: t('Street Address'),
    optional: t('Street Address (Optional)'),
    billingFieldName: 'billingAddressOne',
    deliveryFieldName: 'deliveryAddressOne'
  }),
  addressTwo: (t): AddressConfig => ({
    heading: t('Unit'),
    optional: t('Unit (Optional)'),
    billingFieldName: 'billingAddressTwo',
    deliveryFieldName: 'deliveryAddressTwo'
  }),
  city: (t): AddressConfig => ({
    heading: t('City'),
    optional: t('City (Optional)'),
    billingFieldName: 'billingAddressCity',
    deliveryFieldName: 'deliveryAddressCity'
  }),
  state: (t): AddressConfig => ({
    heading: t('State'),
    optional: t('State (Optional)'),
    billingFieldName: 'billingAddressState',
    deliveryFieldName: 'deliveryAddressState'
  }),
  postal: (t): AddressConfig => ({
    heading: t('Postal Code'),
    optional: t('Postal Code (Optional)'),
    billingFieldName: 'billingAddressPostal',
    deliveryFieldName: 'deliveryAddressPostal'
  }),
  country: (t): AddressConfig => ({
    heading: t('Country'),
    optional: t('Country'),
    billingFieldName: 'deliveryAddressCountry',
    deliveryFieldName: 'deliveryAddressCountry'
  })
}

interface BillingAddressProps {
  storeOrder: { data: Oms.V4.StoreOrder }
  addressFormat: string
  addressRequiredFields: string
  allowEdit: boolean
}

const BillingAddress = ({
  storeOrder = {} as { data: Oms.V4.StoreOrder },
  addressFormat,
  addressRequiredFields,
  allowEdit = false
}: BillingAddressProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const { values } = useFormContext()
  const isPickup = storeOrder.data.isPickup
  const orderedAddressFields = getOrderedAddressFields(
    addressFormat,
    addressRequiredFields
  )

  if (isPickup) {
    return null
  }

  return (
    <React.Fragment>
      <Section>
        <CardHeader>{t('Billing Address')}</CardHeader>
        {allowEdit && (
          <React.Fragment>
            <P bold>{t('Where do we bill you?')}</P>
            <Div width='100%' marginBottom={marginSizes.small}>
              <CheckBox
                name='billingAddressCheckbox'
                content={t('Billing address is the same as delivery address')}
              />
            </Div>
          </React.Fragment>
        )}
        {values.billingAddressCheckbox === false && (
          <Flex flexDirection='column'>
            {orderedAddressFields.map((item, idx) => {
              const config = addressConfig[item.field](t)
              const label = item.isRequired ? config.heading : config.optional
              const name = config.billingFieldName
              return (
                <Form.Input
                  key={idx}
                  type='text'
                  label={label}
                  name={name}
                  disabled={
                    (allowEdit && item.field === 'country') || !allowEdit
                  }
                />
              )
            })}
          </Flex>
        )}
      </Section>
      <Divider />
    </React.Fragment>
  )
}

export default BillingAddress
